import t from 'react-translate';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import NvSelect, { SelectOption } from 'shared/components/inputs/nv-select';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { messageTypeMap } from '../../constants';
import { config } from '@config/pendo.config.json';

const FieldLabel = ({ text }: { text: string }) => (
  <label className='color-gray-2 semi-bold font-size-medium mb-1'>
    {text}
    <span className='color-danger'>*</span>
  </label>
);

const DeliverySection = () => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, matchingType } = mentoringProgram || {};
  const mentoringProgramAliases = useSelector((state) =>
    getMentoringProgramAliases(state, programId)
  );
  const translationBase =
    t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL.EDIT_MODE;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.communications.editCommunicationModal
      .delivery;
  const { watch, setValue } = useFormContext();
  const [toValue, messageTypeValue] = watch(['to', 'messageType']);

  const messageTypeData = useMemo(
    () => messageTypeMap(t, matchingType, mentoringProgramAliases),
    [matchingType, mentoringProgramAliases]
  );

  const selectedMessageType =
    messageTypeValue && messageTypeData[messageTypeValue];
  const { title: selectedMessageTypeTitle, sentOn: selectedMessageTypeSentOn } =
    selectedMessageType || {};

  const targetAudienceOptions: SelectOption[] = useMemo(
    () => [
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTORS_AND_MENTEES(
          mentoringProgramAliases
        ),
        value: TargetAudience.MENTOR_AND_MENTEE,
        dataQa: dataQATag.audience.mentorsMenteesOption,
      },
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTORS(
          mentoringProgramAliases
        ),
        value: TargetAudience.MENTOR,
        dataQa: dataQATag.audience.mentorsOption,
      },
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTEES(
          mentoringProgramAliases
        ),
        value: TargetAudience.MENTEE,
        dataQa: dataQATag.audience.menteesOption,
      },
    ],
    [mentoringProgramAliases]
  );

  const messageTypeOptions = useMemo(
    () =>
      Object.entries(messageTypeData).map(
        ([value, { title, sentOn, dataQa }]) => ({
          label: (
            <div className='d-flex align-items-center'>
              <span className='black'>{title}</span>
              <NvTooltip text={sentOn}>
                <NvIcon
                  icon='info'
                  className='message-type-tooltip ml-2'
                  size='xss-small'
                  tabIndex={0}
                  altLabel={sentOn}
                />
              </NvTooltip>
            </div>
          ),
          value,
          dataQa,
        })
      ),
    [messageTypeData]
  );
  return (
    <div className='edit-communication-delivery p-4 w-100'>
      <div className='d-flex flex-column'>
        <FieldLabel text={translationBase.DELIVERY_SECTION.TO.TITLE()} />
        <NvSelect
          value={toValue}
          className='bg-white align-items-center position-relative mb-2'
          options={targetAudienceOptions}
          onChange={(value) =>
            setValue('to', value, { shouldValidate: true, shouldDirty: true })
          }
          placeholder={translationBase.PLACEHOLDERS.TO()}
          ariaLabel={translationBase.DELIVERY_SECTION.TO.TITLE()}
          required
        />
        <FieldLabel
          text={translationBase.DELIVERY_SECTION.MESSAGE_TYPE.TITLE()}
        />
        <NvSelect
          value={selectedMessageTypeTitle}
          className='bg-white align-items-center position-relative'
          renderFallbackValue
          options={messageTypeOptions}
          onChange={(value) =>
            setValue('messageType', value, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          placeholder={translationBase.PLACEHOLDERS.MESSAGE_TYPE()}
          ariaLabel={translationBase.DELIVERY_SECTION.MESSAGE_TYPE.TITLE()}
          required
        />
        <span className='mt-2 text-small gray-2'>
          {selectedMessageTypeSentOn}
        </span>
      </div>
    </div>
  );
};

export default DeliverySection;
